import { Close } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/Button';
import { Selector } from '@/components/Selector';
import { useYaMetrika } from '@/entities/yaMetrika';
import { useDraftTimelogStore } from '@/features/draftTimelogs';
import { useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { AnalyticsEvent } from '@/types/yametrika';

import s from './FilterDialog.module.scss';

type FilterDialogProps = {
  open: boolean;
  closeModal: () => void;
};

type DraftFilterValue = 'all' | 'logs' | 'drafts';

export const FilterDialog: React.FC<FilterDialogProps> = observer(({ open, closeModal }) => {
  const { filters } = useTimeLogStore();
  const { sendEvent } = useYaMetrika();
  const { taskKeys } = filters;
  const availableKeys = taskKeys.availableKeys;
  const selectedKeys = taskKeys.selectedKeys;
  const { isViewingSelf } = useGlobalUserFilterModel();
  const { draftFilter, setDraftFilter } = useDraftTimelogStore();

  const [localSelectedKeys, setLocalSelectedKeys] = useState<string[]>(selectedKeys);
  const [localDraftFilter, setLocalDraftFilter] = useState<DraftFilterValue>(draftFilter);

  useEffect(() => {
    if (open) {
      setLocalSelectedKeys(selectedKeys);
      setLocalDraftFilter(draftFilter);
    }
  }, [open, selectedKeys, draftFilter]);

  const handleApply = () => {
    sendEvent(AnalyticsEvent.applyFilter);

    if (isViewingSelf) {
      setDraftFilter(localDraftFilter);
    }

    if (localSelectedKeys.length === 0) {
      taskKeys.setSelectedKeys([]);
      taskKeys.reset();
      closeModal();

      return;
    }

    taskKeys.setSelectedKeys(localSelectedKeys);
    taskKeys.apply();
    closeModal();
  };

  const handleCancel = () => {
    setLocalSelectedKeys(selectedKeys);
    setLocalDraftFilter(draftFilter);
    closeModal();
  };

  const hasChanges = useMemo(() => {
    const draftChanged = isViewingSelf && localDraftFilter !== draftFilter;

    if (draftChanged) return true;

    if (localSelectedKeys.length === 0 && selectedKeys.length === 0) return false;

    if (localSelectedKeys.length === 0 || selectedKeys.length === 0) return true;

    const localSet = new Set(localSelectedKeys);
    const selectedSet = new Set(selectedKeys);

    if (localSet.size !== selectedSet.size) return true;

    return (
      localSelectedKeys.some((key) => !selectedSet.has(key)) ||
      selectedKeys.some((key) => !localSet.has(key))
    );
  }, [draftFilter, isViewingSelf, localDraftFilter, localSelectedKeys, selectedKeys]);

  const isInitialState = useMemo(() => {
    const queuesDefault = localSelectedKeys.length === 0;
    const draftsDefault = !isViewingSelf || localDraftFilter === 'all';

    return queuesDefault && draftsDefault;
  }, [isViewingSelf, localDraftFilter, localSelectedKeys.length]);

  const handleReset = () => {
    setLocalSelectedKeys([]);

    if (isViewingSelf) {
      setLocalDraftFilter('all');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      fullWidth
      maxWidth="sm"
      sx={{
        '.MuiPaper-root': {
          borderRadius: '12px',
        },
      }}
    >
      <div className={s.dialog__header}>
        <h1 className={s.dialog__title}>Фильтры</h1>
        <IconButton onClick={closeModal} size="small">
          <Close />
        </IconButton>
      </div>
      <DialogContent>
        <div className={s.dialog__content}>
          {isViewingSelf && (
            <>
              <p>Тип записей</p>
              <ToggleButtonGroup
                exclusive
                value={localDraftFilter}
                onChange={(_, value: DraftFilterValue | null) => {
                  if (value !== null) {
                    setLocalDraftFilter(value);
                  }
                }}
                size="small"
                className={s.dialog__draft_toggle}
              >
                <ToggleButton value="all" className={s.dialog__draft_toggle_btn}>
                  Все
                </ToggleButton>
                <ToggleButton value="logs" className={s.dialog__draft_toggle_btn}>
                  Только логи
                </ToggleButton>
                <ToggleButton value="drafts" className={s.dialog__draft_toggle_btn}>
                  Только черновики
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
          <p>Очереди</p>
          <Selector
            options={availableKeys.map((key) => ({ id: key, label: key, value: key }))}
            value={localSelectedKeys.map((key) => ({ id: key, label: key, value: key }))}
            onChange={(options) => {
              setLocalSelectedKeys(options.map((option) => option.value));
            }}
          />
        </div>
      </DialogContent>

      {!isInitialState && !hasChanges && (
        <DialogActions>
          <Button onClick={handleReset} variant="outlined">
            Сброс фильтра
          </Button>
        </DialogActions>
      )}

      {hasChanges && (
        <DialogActions>
          {!isInitialState && (
            <Button onClick={handleReset} variant="outlined" sx={{ borderRadius: '8px' }}>
              Сброс фильтра
            </Button>
          )}
          <Button onClick={handleCancel} variant="outlined" sx={{ borderRadius: '8px' }}>
            Отменить
          </Button>
          <Button onClick={handleApply} variant="contained" sx={{ borderRadius: '8px' }}>
            Применить
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
});
